import { makeStyles } from "@material-ui/core"
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js"
import React, { FC } from "react"
import { Enum_Activity_Category_Enum } from "../../generated/graphql"
import { alertsAndBadges } from "../../utils/imageMapping"
import { colors } from "../../utils/theme"
import Button from "./button/Button"

/**
 * A React functional component that renders a summary card for an activity.
 * The card displays the activity's icon, name, description, and provides buttons
 * for interacting with the activity, such as trying it or toggling the visibility
 * of the full summary.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {RealmActivity} [props.activity] - The activity data to display in the card.
 * @param {boolean} props.isEngageActivity - A flag indicating whether the activity is an "Engage" activity.
 *
 * @returns {JSX.Element} The rendered activity summary card component.
 *
 * @example
 * <ActivitySummaryCard activity={activity} isEngageActivity={true} />
 *
 * @remarks
 * - The component uses Recoil for state management to retrieve the current theme.
 * - It tracks user interactions using App Insights and navigates to the "ActivityViewer" screen when the "Try Activity" button is clicked.
 * - The "Show More" button toggles the visibility of the full activity description.
 */

interface Activity {
  id: string // UUID as string
  category: Enum_Activity_Category_Enum
  sub_category_id: string // UUID as string
  display_name: string
  description: string
  icon_uri?: string | null // optional field
  header_uri?: string | null // optional field
  created_at: string // ISO date string
  updated_at: string // ISO date string
}

export const ActivitySummaryCard: FC<{
  activity?: Activity
  isEngageActivity: boolean
}> = ({ activity }) => {
  const styles = useStyles()
  const [showFullSummary, setShowFullSummary] = React.useState(false)
  const appInsights = useAppInsightsContext()

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        <img
          src={alertsAndBadges[activity?.icon_uri ?? "self_improvement"]}
          alt={activity?.display_name || "Activity icon"}
          style={{ width: 24, height: 24 }}
        />
        <div className={styles.text}>
          <span className={styles.title}>{activity?.display_name}</span>
          <p
            className={`${styles.summaryText} ${
              showFullSummary ? "" : styles.truncatedText
            }`}
          >
            {activity?.description}
          </p>
        </div>
      </div>
      <div className={styles.footer}>
        <Button
          className={`ghost-button ${styles.button}`}
          onClick={() => {
            setShowFullSummary(!showFullSummary)
          }}
          customVariant={"ghost"}
        >
          <p className={`${styles.buttonText}`}>
            {showFullSummary ? "Show Less" : "Show More"}
          </p>
        </Button>
      </div>
    </div>
  )
}

const useStyles = makeStyles({
  container: {
    marginHorizontal: 8,
    marginVertical: 4,
    padding: 16,
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${colors.neutral.clay}`,
  },
  text: {
    marginLeft: 8,
    flex: 1,
  },
  title: {
    color: "#0B1950",
    fontSize: 18,
    fontWeight: 700,
  },
  summaryText: {
    fontWeight: 400,
    fontSize: 14,
    color: "#424F80",
    position: "relative",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  buttonText: {
    color: "#4F66AA",
    fontSize: 14,
    margin: 16,
  },
  footer: {
    marginTop: 8,
    marginLeft: 4,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  detailsText: {
    color: "#737373",
    marginLeft: 4,
  },
  button: {
    height: 32,
    borderRadius: 16,
    border: "2px solid #4F66AA",
  },
  truncatedText: {
    overflow: "hidden",
    maxHeight: 60,
    position: "relative",
    "&:after": {
      content: "''",
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      height: 18,
      background: "linear-gradient(to bottom, rgba(255,255,255,0), #fff 90%)",
      pointerEvents: "none",
    },
  },
})
